import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Instagram, Facebook, ExternalLink } from "lucide-react";
import { SubpageHeader } from "../components/SubpageHeader";
import { Reveal } from "../components/Reveal";
import { ARTISTS } from "../data/artistas";
import { useLanguage } from "../i18n/LanguageContext";
import { useSEO, breadcrumbLd, personLd } from "../components/SEO";

const PressLinks = ({ artist, label }) => (
    <>
        <p className="font-caps text-[11px] tracking-[0.35em] uppercase gold-text">{label}</p>
        <div className="flex items-center gap-3 mt-3 mb-5">
            <span className="h-px w-40 bg-[#c9a24b]/50" />
            <img src="/images/ornamento.webp" alt="" aria-hidden="true" className="w-8 opacity-80" />
        </div>
        <ul className="space-y-3.5">
            {artist.press.map((p) => (
                <li key={p.url}>
                    <a
                        href={p.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`artist-press-${p.label.slice(0, 12).replace(/\s/g, "-").toLowerCase()}`}
                        className="group inline-flex items-center gap-3 font-display font-light text-base sm:text-lg text-[#efe6d6]/75 hover:text-[#c9a24b] transition-colors duration-300"
                    >
                        <ExternalLink size={14} className="text-[#c9a24b]/60 group-hover:text-[#c9a24b] shrink-0" />
                        {p.label}
                    </a>
                </li>
            ))}
        </ul>
    </>
);

export default function ArtistPage() {
    const { slug } = useParams();
    const { t } = useLanguage();
    const artist = ARTISTS.find((a) => a.slug === slug);

    useSEO({
        title: artist ? `${artist.name} | Fados à Mesa` : t.seo.home.title,
        description: artist ? (t.artists[slug]?.bio || artist.bio).split("\n")[0] : t.seo.home.desc,
        jsonLd: artist
            ? [
                  personLd(artist, t.artists[slug]?.role || artist.role),
                  breadcrumbLd([
                      { name: t.bc.home, path: "/" },
                      { name: t.bc.artistas, path: "/#artistas" },
                      { name: artist.name, path: `/artistas/${artist.slug}` },
                  ]),
              ]
            : undefined,
    });

    if (!artist) {
        return (
            <div className="min-h-screen bg-[#070503] text-[#efe6d6] relative flex items-center justify-center px-6" data-testid="artist-page">
                <SubpageHeader />
                <p className="font-display italic font-light text-xl text-[#efe6d6]/60">{t.artistPage.naoEncontrado}</p>
            </div>
        );
    }

    const role = t.artists[slug]?.role || artist.role;
    const bio = t.artists[slug]?.bio || artist.bio;

    return (
        <div className="min-h-screen bg-[#070503] text-[#efe6d6] relative" data-testid="artist-page">
            <div className="relative min-h-screen overflow-hidden flex items-center">
                <motion.div
                    initial={{ opacity: 0, scale: 1.05 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute inset-0"
                >
                    <img
                        src={artist.image}
                        alt={`${artist.name} — ${role}`}
                        className="absolute left-0 top-0 h-full w-auto max-w-none object-cover object-top"
                        data-testid="artist-page-image"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent from-30% via-[#070503]/70 via-60% to-[#070503] to-85%" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#070503] via-transparent to-[#070503]/40" />
                </motion.div>

                <SubpageHeader />

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    className="relative z-10 mx-auto max-w-[1500px] w-full px-5 sm:px-10 pt-52 sm:pt-36 pb-40 grid lg:grid-cols-2"
                >
                    <div className="hidden lg:block" />
                    <div className="max-w-xl border border-[#c9a24b]/25 bg-[#070503]/65 backdrop-blur-sm px-6 py-10 sm:px-10" data-testid="artist-text-panel">
                        <div className="flex justify-center">
                            <img src="/images/ornamento.webp" alt="" aria-hidden="true" className="w-16 opacity-90" />
                        </div>
                        <h1
                            className="font-display font-medium gold-text text-4xl sm:text-5xl tracking-[0.08em] text-center mt-4"
                            data-testid="artist-page-name"
                        >
                            {artist.name.toUpperCase()}
                        </h1>
                        <p className="font-display text-sm sm:text-base tracking-[0.35em] uppercase text-[#efe6d6]/85 text-center mt-3">
                            {role}
                        </p>
                        <div className="flex items-center justify-center gap-3 my-7">
                            <span className="h-px w-24 bg-[#c9a24b]/50" />
                            <img src="/images/ornamento.webp" alt="" aria-hidden="true" className="w-10 opacity-80" />
                            <span className="h-px w-24 bg-[#c9a24b]/50" />
                        </div>
                        {bio.split("\n\n").map((p, i) => (
                            <p
                                key={i}
                                className="font-display font-light text-lg sm:text-xl text-[#e8dcc3]/90 leading-loose mt-5 first:mt-0"
                                data-testid={i === 0 ? "artist-page-bio" : undefined}
                            >
                                {p}
                            </p>
                        ))}
                        <div className="flex flex-wrap gap-4 mt-9" data-testid="artist-page-social">
                            <a
                                href={artist.instagram}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="artist-page-instagram"
                                className="inline-flex items-center gap-3 font-caps text-[11px] tracking-[0.3em] uppercase text-[#c9a24b] border border-[#c9a24b]/60 bg-[#070503]/30 backdrop-blur-sm px-7 py-3 hover:bg-[#c9a24b] hover:text-[#070503] transition-colors duration-400"
                            >
                                <Instagram size={15} strokeWidth={1.5} />
                                Instagram
                            </a>
                            {artist.facebook && (
                                <a
                                    href={artist.facebook}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="artist-page-facebook"
                                    className="inline-flex items-center gap-3 font-caps text-[11px] tracking-[0.3em] uppercase text-[#c9a24b] border border-[#c9a24b]/60 bg-[#070503]/30 backdrop-blur-sm px-7 py-3 hover:bg-[#c9a24b] hover:text-[#070503] transition-colors duration-400"
                                >
                                    <Facebook size={15} strokeWidth={1.5} />
                                    Facebook
                                </a>
                            )}
                        </div>
                    </div>
                </motion.div>

                {artist.press.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.9, duration: 1 }}
                        className="hidden lg:block absolute bottom-8 left-10 right-10 z-10"
                        data-testid="artist-page-press"
                    >
                        <PressLinks artist={artist} label={t.artistPage.imprensa} />
                    </motion.div>
                )}
            </div>

            {artist.press.length > 0 && (
                <div className="lg:hidden relative z-10 mx-auto max-w-[1200px] px-5 pt-6 pb-20" data-testid="artist-page-press-mobile">
                    <PressLinks artist={artist} label={t.artistPage.imprensa} />
                </div>
            )}

            {artist.videos.length > 0 && (
                <div className="relative z-10 mx-auto max-w-[1200px] px-5 sm:px-10 pb-24" data-testid="artist-page-videos">
                    <Reveal>
                        <p className="font-caps text-[11px] tracking-[0.35em] uppercase text-[#c9a24b]">{t.artistPage.emPalco}</p>
                        <h2 className="font-display font-light text-3xl sm:text-4xl text-[#efe6d6] mt-3">
                            {t.artistPage.oQueA}<span className="gold-text">{t.artistPage.oQueGold}</span>{t.artistPage.oQueB}
                        </h2>
                    </Reveal>
                    <div className={`mt-8 grid gap-6 ${artist.videos.length > 1 ? "md:grid-cols-2" : "max-w-3xl"}`}>
                        {artist.videos.map((v) => (
                            <Reveal key={v.id}>
                                <div className="portrait-frame overflow-hidden" data-testid={`artist-video-${v.id}`}>
                                    <div className="aspect-video">
                                        <iframe
                                            src={`https://www.youtube-nocookie.com/embed/${v.id}`}
                                            title={v.title}
                                            loading="lazy"
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen
                                            className="w-full h-full"
                                        />
                                    </div>
                                </div>
                                <p className="font-display italic font-light text-sm text-[#efe6d6]/60 mt-3 text-center">{v.title}</p>
                            </Reveal>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
